import omit from 'lodash/omit'
import lowerCase from 'lodash/lowerCase'
import isEqual from 'lodash/isEqual'
import { catalogModifiers, pdpLinksConfig, MATTRESS_REMOVAL_SKUS, EXTEND_SKU, EXCLUDED_SKUS, PRODUCT_CODES, CATEGORIES } from '@/config/globals'
import { getVariants } from '@/services/catalog'

export const pillowBundleSkus = {
    'P27D31CF18-SQ': 'P27D31CF18-2PKQ', // The Saatva Pillow in Queen, 2-Pack Bundle high loft
    'P27D31CF18-K': 'P27D31CF18-2PKK', // The Saatva Pillow in King, 2-Pack Bundle high loft
    'SAOLXPLWLL-Q': 'SAOLXPLL2PK-Q', // The Saatva Pillow in Queen, 2-Pack Bundle standard loft
    'SAOLXPLWLL-K': 'SAOLXPLL2PK-K', // The Saatva Pillow in King, 2-Pack Bundle standard loft
    'SAAGRPHPLW-Q': 'SAAGRPHPLW2PK-Q', // Memory Foam Pillow in Queen, 2-Pack Bundle
    'SAAGRPHPLW-K': 'SAAGRPHPLW2PK-K', // Memory Foam Pillow in King, 2-Pack Bundle
    'SAA5050PLW-Q': 'SAA5050PLW2PK-Q', // Down Alternative Pillow in Queen, 2-Pack Bundle
    'SAA5050PLW-K': 'SAA5050PLW2PK-K', // Down Alternative Pillow in King, 2-Pack Bundle
    'SACLDMFNPLW-Q': 'SACLDMFNP2PK-Q', // Cloud Memory Foam Pillow in Queen, 2-Pack Bundle
    'SACLDMFNPLW-K': 'SACLDMFNP2PK-K', // Cloud Memory Foam Pillow in King, 2-Pack Bundle
    'SAORGQPLW-Q': 'SAORGQPL2PK-Q', // Organic Quilted Pillow in Queen, 2-Pack Bundle
    'SAORGQPLW-K': 'SAORGQPL2PK-K', // Organic Quilted Pillow in King, 2-Pack Bundle
    'SAAMFPLW-Q': 'SAAMFPLW-2PKQ', // Cloud Memory Foam Pillow in King, Standard loft, 2-Pack Bundle
    'SAAMFPLW-K': 'SAAMFPLW-2PKK', // Cloud Memory Foam Pillow in Queen, Standard loft, 2-Pack Bundle
    'SAAGMFPLW-Q': 'SAAGMFPLW-2PKQ', // Memory Foam Pillow in Queen, Standard Loft, 2-Pack Bundle
    'SAAGMFPLW-K': 'SAAGMFPLW-2PKK', // Memory Foam Pillow in King, Standard Loft, 2-Pack Bundle
    'SAAOQPLW-Q': 'SAAOQPLW-2PKQ', // Organic Quilted Pillow in Queen, Standard Loft, 2-Pack Bundle
    'SAAOQPLW-K': 'SAAOQPLW-2PKK', // Organic Quilted Pillow in King, Standard Loft, 2-Pack Bundle
    'SAA5050STDPLW-Q': 'SAA5050STDPLW2Q', // Down Alternative Pillow in King Standard loft, 2-Pack Bundle
    'SAA5050STDPLW-K': 'SAA5050STDPLW2K' // Down Alternative Pillow in Queen Standard loft, 2-Pack Bundle
}

export const queenPillowSkus = [
    'P27D31CF18-SQ',
    'SAOLXPLWLL-Q',
    'SAAGRPHPLW-Q',
    'SAA5050PLW-Q',
    'SACLDMFNPLW-Q',
    'SAORGQPLW-Q',
    'SAAMFPLW-Q',
    'SAAGMFPLW-Q',
    'SAAOQPLW-Q',
    'SAA5050STDPLW-Q'
]

export const kingPillowSkus = [
    'P27D31CF18-K',
    'SAOLXPLWLL-K',
    'SAAGRPHPLW-K',
    'SAA5050PLW-K',
    'SACLDMFNPLW-K',
    'SAORGQPLW-K',
    'SAAMFPLW-K',
    'SAAGMFPLW-K',
    'SAAOQPLW-K',
    'SAA5050STDPLW-K'
]

export const pillowDiscounts = {
    queen: 40,
    king: 60
}

export const pillows = [
    'the-saatva-pillow',
    'memory-foam-pillow',
    'down-alternative-pillow',
    'cloud-memory-foam-pillow',
    'organic-quilted-pillow'
]

export const isPillowBundle = (sku) => Object.values(pillowBundleSkus).includes(sku)

export const isPillowSku = (sku) => {
    return pillows.includes(sku) || queenPillowSkus.includes(sku) || kingPillowSkus.includes(sku)
}

export const canBeABundle = (product) => {
    return !isPillowBundle(product.sku) && isPillowSku(product.productCode)
}

export const isKingPillow = (product) => {
    return kingPillowSkus.includes(product.sku)
}

export const getOriginalPillowBundleSku = (sku = '') => {
    const values = Object.values(pillowBundleSkus)
    const keys = Object.keys(pillowBundleSkus)
    const index = values.indexOf(sku)
    return keys[index]
}

/**
 * Adds sortKeys and adds product and compare URIs
 * @param {object} product
 * @returns
 */
const formatProductDetails = (product) => {
    let details = {}
    product.details &&
        product.details.forEach((detail, index) => {
            const key = detail.key
            const name = detail.name
            const features = omit(detail, ['key', 'name'])

            // This key (feature, firmness) has been added so we push into content array!
            if (details[key] && details[key].content && details[key].content.length > 0) {
                details[key].content.push(features)
            } else {
                details = {
                    ...details,
                    [key]: {
                        sortOrder: index,
                        key,
                        name,
                        content: [features]
                    }
                }
            }
        })
    const detailsList = Object.values(details).sort((a, b) => a.sortOrder - b.sortOrder)
    return detailsList
}

const filterDetails = (details = [], keys = []) => {
    if (!keys.length) {
        return details
    }

    return details.filter(({ key }) => keys.includes(key))
}

/**
 * Merges prismic product content with catalog product data
 * @param {object} catalogProduct
 * @param {object} contentProduct
 * @param {string[]} keys
 * @returns
 */
export const formatProduct = (catalogProduct, contentProduct, keys = [], selectedProduct = {}) => {
    /*
        copy of catalogProduct with more elements on the QTY dropdown values
        to allow selection of up to 10 items from detail modal
    */
    const newCatalogProduct = {
        ...catalogProduct,
        options: catalogProduct.options.map((opt) => {
            if (opt.code === 'quantity') {
                return getQuantityOption()
            }
            return opt
        })
    }
    const filterdContent = {
        ...contentProduct,
        details: filterDetails(contentProduct.details, keys)
    }

    const pillowBundleOverrides = getPillowBundleOverrides(catalogProduct, selectedProduct)

    return {
        ...newCatalogProduct,
        ...contentProduct,
        ...pillowBundleOverrides,
        details: formatProductDetails(filterdContent),
        hasLearnMoreCta: false
    }
}

const getQuantityOption = (maxQuantity = 10) => {
    const values = []

    for (let i = 1; i <= maxQuantity; i++) {
        values.push({
            code: i.toString(),
            label: i.toString(),
            swatch: null
        })
    }

    return {
        code: 'quantity',
        displayName: 'Qty',
        displayType: null,
        default: 1,
        values
    }
}

/**
 * For Pillow Pair bundles some values need to be updated as the default
 * information in the Catalog Service is intended for single products
 * @param {*} catalogProduct
 * @param {*} selectedProduct
 * @returns {Object}
 */
const getPillowBundleOverrides = (catalogProduct = {}, selectedProduct = {}) => {
    if (isPillowBundle(selectedProduct.sku)) {
        // Get only bundle variants
        const variants =
            catalogProduct.variants.filter((variant) => isPillowBundle(variant.sku)) || []
        // Get price range for pillow pairs
        const prices = variants.map(({ price }) => price).sort((prev, next) => prev - next)

        return {
            variants,
            lowestPrice: prices[0],
            highestPrice: prices[prices.length - 1]
        }
    }
    return {}
}

const STANDARD_QUEEN = 'Standard/Queen'
const SHORT_MATTRESS = '11.5'
const TALL_MATTRESS = '14.5'

/**
 * Transform attribute value
 * Source: service-cart-checkout/app/dataSources/CatalogService/index.js:103
 * @param {string} value
 * @returns {string}
 */
export const transformAttributeValue = (value) => {
    if (value === STANDARD_QUEEN) {
        return 'Queen'
    }
    if (value === SHORT_MATTRESS) {
        return '11.5"'
    }
    if (value === TALL_MATTRESS) {
        return '14.5"'
    }
    return value
}

/**
 * Get attributes of the selected variant
 * @param {Object} selectedProduct
 * @param {Object} productData
 * @returns {Object.<string, string>}
 */
export const getVariantAttributes = (selectedProduct, productData) => {
    const selectedProductAttributeValues = selectedProduct.productAttributes
        ? selectedProduct.productAttributes.map(({ value }) => value)
        : [] // Recommended product doesn't have productAttributes

    const selectedVariant = productData.variants.find((variant) => {
        const variantAttributes = variant.attributes.map((attr) =>
            transformAttributeValue(attr.value)
        )

        // Check if the variant is in stock then check if the selected product attributes are included in the variant attributes
        return variant.inStock && selectedProductAttributeValues.every(value => variantAttributes.includes(value))
    })

    if (selectedVariant) {
        return selectedVariant.attributes.reduce((acc, attr) => {
            acc[attr.code] = attr.value
            return acc
        }, {})
    }

    return {}
}

export const compareAttributes = (selectedProduct = {}, productState = {}) => {
    const selectedProductAttributes = selectedProduct.productAttributes
        ? selectedProduct.productAttributes.map(({ value }) => value)
        : [] // Recommended product doesn't have productAttributes

    const variantAttributes = productState.attributes.map(({ value }) =>
        transformAttributeValue(value)
    )

    return isEqual(selectedProductAttributes, variantAttributes)
}

/**
 * Get the product modifiers
 * @param {Object} product Selected cart product
 * @returns {string[]} List of product modifiers
 */
export const getProductModifiers = (product) => {
    const isBundle = isPillowBundle(product.sku) && isPillowSku(product.productCode)

    let MODIFIERS = [
        catalogModifiers.EXCLUDE_QUILT_SETS,
        catalogModifiers.PRE_CART,
        catalogModifiers.EXCLUDE_PILLOW_BUNDLES
    ]

    // allows the catalog service to return all variants including pillow pair bundles
    if (isBundle) {
        return [catalogModifiers.EXCLUDE_QUILT_SETS]
    }

    return MODIFIERS
}

export const formatProductContent = (categoryContent = [], selectedProduct = {}) => {
    const content = categoryContent.find(({ sku }) => selectedProduct.productCode === sku)

    // Replace name to display only for pillow pair bundles
    if (content && isPillowBundle(selectedProduct.sku)) {
        content.title = selectedProduct.genericName
    }

    const defaultContent = {
        // Some products don't have content. Provide the minimum necessary for the modal to work
        title: selectedProduct.genericName || selectedProduct.name
    }

    return content || defaultContent
}

export const getPillowPairDiscount = (product) => {
    const pillowSizeData = product.productAttributes && product.productAttributes.find(att => att.name === 'Size')
    const pillowSizeValue = pillowSizeData && pillowSizeData.value.toLowerCase()
    return pillowDiscounts[pillowSizeValue]
}

/**
 * Perfeform some checks to determine if the item has PDP
 * @param {CartItem} item
 * @returns {boolean}
 */
export const hasPDPLink = (item = {}) => {
    // Products that meet these conditions have no PDP
    const excludedByType = pdpLinksConfig.excludedTypes.includes(lowerCase(item.magentoProductType))
    const excludedBySubcategory = pdpLinksConfig.excludedSubcategories.includes(lowerCase(item.subcategory))
    const excludedByProductCode = pdpLinksConfig.excludedProductCodes.includes(item.productCode)
    const excludeMattressRemoval = MATTRESS_REMOVAL_SKUS.includes(item.sku)

    if (excludedByType || excludedBySubcategory || excludedByProductCode || excludeMattressRemoval) {
        // There are a few exceptions for specific products that do have PDPs
        return pdpLinksConfig.allowedProductCodes.includes(item.productCode)
    }

    return true
}

/**
 * Concatenates an array of names into a single string, with commas separating
 * each name and an '&' before the last name.
 *
 * @param {string[]} names - An array of names to be concatenated.
 * @returns {string} A formatted string of names.
 */
export const concatenateNames = (names = []) => {
    if (names.length === 0) return ''
    if (names.length === 1) return names[0]

    const allButLast = names.slice(0, -1).join(', ')
    const last = names[names.length - 1]

    return `${allButLast} & ${last}`
}

/**
 * If the item is a pillow-pair bundle
 * Return the original single-pillow sku to be able
 * to check the bundle status
 * @param {Object} item Cart item
 * @returns {String}
 */
export const getItemSkuForBundle = (item) => isPillowSku(item.productCode) ? getOriginalPillowBundleSku(item.sku) : item.sku


/**
 * Get the product codes for the bundle
 * @param {Array} relationships
 * @returns {Array}
 */
export const getBundleProductCodes = (relationships = []) => {
    return relationships.reduce((codes, relationship) => {
        if (relationship.relationshipLabel === 'bundledProducts') {
            relationship.relatedProductCodes.forEach((code) => {
                codes.push(code)
            })
        }
        return codes
    }, [])
}

/**
 * Check if the item exists in another bundle
 * @param {Object} item
 * @param {Array} bundlesAcc
 * @returns {Boolean}
 */
export const itemExistsInOtherBundle = (item = {}, bundlesAcc = []) => {
    return !!bundlesAcc.find((bundle) => {
        return bundle.items.find((bundledItem) => bundledItem.sku === item.sku)
    })
}

export const transformCatalogAttributes = (attributes = []) => {
    if (attributes) {
        return attributes.map(({ label, value }) => ({ name: label, value: transformAttributeValue(value) }))
    }
    return []
}

/**
 * Retrieve product attributes for extend item
 * @param {string} productSku Product sku
 * @returns {Promise<Array>} Array of product attributes
 */
export const getProductAttributesForExtend = async (productSku = '') => {
    if (!productSku) return
    const variants = await getVariants([productSku])

    if (!variants?.length) return []
    const product = variants[0]

    const productNameAttr = {
        name: 'Product',
        value: product.genericName
    }
    const cleanedAttributes = transformCatalogAttributes(product.attributes)

    return [productNameAttr, ...cleanedAttributes]
}

export const isExcludedItem = (item = {}) => {
    const sku = item.sku

    if (sku === EXTEND_SKU) {
        if (!!item.leadToken) {
            return false
        }
        return true
    }

    return EXCLUDED_SKUS.includes(sku)
}

export const isExtendPostPurchaseItem = (item = {}) => {
    return item.sku === EXTEND_SKU && !!item.leadToken
}

/**
 * Check if cart has solaire
 * @param {Array} items Cart items
 * @returns {Boolean}
 */
export const hasSolaire = (items = []) => {
    return items?.some(({ productCode }) => productCode === PRODUCT_CODES.solaire)
}

export const hasFurniture = (items = []) => {
    return items?.some(({ category }) => category === CATEGORIES.FURNITURE)
}

export const transformProductDataForDiscountsData = (items = []) => {
    if (items.length === 0) return []
    return items.map((product) => {
        let originalPillowSku = null
        if (product?.subcategory?.toLowerCase() === 'pillows') {
            const isPillowPair = isPillowBundle(product?.sku)
            originalPillowSku = isPillowPair ? getOriginalPillowBundleSku(product?.sku) : product?.sku
        }
        const promotionsData = {
            parentSku: product?.productCode,
            productCode: product?.productCode,
            sku: originalPillowSku || product?.sku,
            quantity: originalPillowSku ? product?.quantity * 2 : product?.quantity,
            category: product?.category?.toLowerCase(),
            subCategory: product?.subcategory?.toLowerCase(),
            price: product?.price,
        }

        return  promotionsData
    })
}

export const sortDiscountsByType = (discountSummary = {}, sitewidePromo = {}, appliedDiscounts = []) => {
    if (!discountSummary || !sitewidePromo) return {sitewideDiscounts: [], skuLevelDiscounts: [], couponDiscounts: []}
    // Determine the type of sitewide promo which includes the coupon discount and sitewide discount.
    // The promoProfile sometimes does not contain all the correct values when the coupon stacks with the sitewide promo, so we need to check the actual discount amounts for the BMSM, fixed, and percentage specifically.
    let sitewidePromoType = ''
    if (sitewidePromo?.isBMSMPromo || sitewidePromo?.bmsmAmounts?.length > 0) {
        sitewidePromoType = 'bmsm'
    } else if (sitewidePromo?.isFixedPromo || sitewidePromo?.fixedAmounts?.length > 0) {
        sitewidePromoType = 'fixed_off'
    } else if (sitewidePromo?.isPercentagePromo || sitewidePromo?.percentAmounts?.length > 0) {
        sitewidePromoType = 'percentage_off'
    } else {
        // If the type is not a bmsm, fixed, or percentage, then look through the remaining options from the promo profile and add the type.
        // There should only be one active type after remo
        Object.entries(sitewidePromo.promoProfile).forEach(([key, value]) => {
            const excludedKeys = ['isCouponPromo', 'isBMSMPromo', 'isFixedPromo', 'isPercentagePromo']
            if (value && !excludedKeys.includes(key)) {
                sitewidePromoType = key
            }
        })
    }

    // Looks at discounts on cart item and compares to the promotion service discount label breakdown.
    // When there is a stacked coupon and sitewide promo, the sitewide promo will NOT be included in the promotion service.
    const {sitewideDiscounts, skuLevelDiscounts, couponDiscounts} = appliedDiscounts.reduce((acc, discount) => {
        const discountType = discountSummary?.labelBreakdown?.find(label => label.label === discount.label || discount.label.includes(label.label))?.discountType
        if (discountType === 'value' || discountType === 'surplus' || discountType === 'included') {
            const skuLevelDiscount = {
                label: discount.label,
                type: `sku_${discountType}_discount`
            }
            acc.skuLevelDiscounts.push(skuLevelDiscount)
        } else if (sitewidePromo?.promoProfile.isCouponPromo && sitewidePromo.labels.includes(discount.label)) {
            // Sort the coupon discounts.
            const couponDiscount = {
                label: discount.label,
                type: `coupon_${sitewidePromoType}`,
                couponCode: sitewidePromo.couponCode
            }
            acc.couponDiscounts.push(couponDiscount)
        } else {
            // Sort the sitewide discounts.
            const isStackedWithCoupon = sitewidePromo?.promoProfile.isCouponPromo || sitewidePromo?.couponCode
            const sitewideDiscount = {
                label: discount.label,
                type: `sitewide_${isStackedWithCoupon ? 'stacked_unknown' : sitewidePromoType}`,
                isStackedWithCoupon: isStackedWithCoupon
            }
            acc.sitewideDiscounts.push(sitewideDiscount)
        }
        return acc
       
    }, {sitewideDiscounts: [], skuLevelDiscounts: [], couponDiscounts: []})

    return {sitewideDiscounts, skuLevelDiscounts, couponDiscounts}
}
